// src/utils/linkUtils.ts
import { getRewriteXLinksSetting } from './settings';

// Define the base URL for the current application instance
export const APP_BASE_URL = window.location.origin; // e.g., "http://localhost:5173" or "https://yourdomain.com"

/**
 * Rewrites x.com URLs to xcancel.com if the setting is enabled.
 * Returns the original URL otherwise, or an empty string for invalid input.
 * Handles both http/https and www/non-www variants of x.com.
 *
 * @param url The URL string to potentially rewrite. Can be undefined or null.
 * @returns The processed URL string, or an empty string if input was null/undefined.
 */
export const rewriteXLink = (url: string | undefined | null): string => {
    // Return empty string immediately if URL is null, undefined, or empty
    if (!url) return '';

    const shouldRewrite = getRewriteXLinksSetting();

    // Only proceed with parsing if rewriting is enabled
    if (shouldRewrite) {
        try {
            // Use a temporary base URL if the input URL is relative (e.g., /path)
            // This helps the URL constructor parse it correctly.
            const fullUrl = url.startsWith('/') ? `https://example.com${url}` : url;
            const parsedUrl = new URL(fullUrl);

            // Check if the hostname is exactly 'x.com' or 'www.x.com'
            if (parsedUrl.hostname === 'x.com' || parsedUrl.hostname === 'www.x.com') {
                // Change the hostname
                parsedUrl.hostname = 'xcancel.com';
                // Return the modified URL string
                // If the original URL was relative, return only the path and query params
                return url.startsWith('/') ? parsedUrl.pathname + parsedUrl.search + parsedUrl.hash : parsedUrl.toString();
            }
        } catch (e) {
            // Ignore invalid URLs or URLs that can't be parsed.
            // console.warn("Could not parse URL for x.com rewriting:", url, e);
        }
    }

    // Return the original URL if rewriting is disabled or parsing failed/hostname didn't match
    return url;
};


/**
 * Rewrites reddit.com URLs (posts and subreddits) to point to the current application's routes.
 * Handles http/https, www/non-www, and old.reddit.com variants.
 * Does not rewrite other reddit.com URLs (e.g., user profiles, settings).
 *
 * @param url The URL string to potentially rewrite. Can be undefined or null.
 * @returns The processed URL string (potentially internal), or the original URL.
 */
export const rewriteRedditLinks = (url: string | undefined | null): string => {
    if (!url) return '';

    try {
        // Use a temporary base if relative
        const fullUrl = url.startsWith('/') ? `https://www.reddit.com${url}` : url;
        const parsedUrl = new URL(fullUrl);
        const hostname = parsedUrl.hostname.toLowerCase();

        // Check if it's a reddit domain we want to handle
        if (hostname === 'reddit.com' || hostname === 'www.reddit.com' || hostname === 'old.reddit.com') {
            const path = parsedUrl.pathname;
            const postRegex = /^\/r\/([^/]+)\/comments\/([^/]+)/;
            const subredditRegex = /^\/r\/([^/]+)\/?$/;

            const postMatch = path.match(postRegex);
            const subredditMatch = path.match(subredditRegex);

            if (postMatch) {
                // It's a post link, rewrite to internal path
                // Keep the rest of the path (e.g., /comment/id) and hash
                const remainingPathAndHash = path.substring(postMatch[0].length);
                // return `${APP_BASE_URL}${path}${parsedUrl.search}${parsedUrl.hash}`; // Keep full path
                return `${path}${remainingPathAndHash}${parsedUrl.search}${parsedUrl.hash}`; // Return relative path for react-router
            } else if (subredditMatch) {
                // It's a subreddit link, rewrite to internal path
                // return `${APP_BASE_URL}${path}${parsedUrl.search}${parsedUrl.hash}`; // Keep full path
                 return `${path}${parsedUrl.search}${parsedUrl.hash}`; // Return relative path
            }
            // Add other potential rewrites here (e.g., user profiles if implemented)
        }
    } catch (e) {
        // Ignore invalid URLs
        // console.warn("Could not parse URL for reddit.com rewriting:", url, e);
    }

    // Return the original URL if it wasn't a match or parsing failed
    return url;
};